import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useCallback, useMemo, useRef, useState } from 'react';
import { useClientLayoutEffect } from '../hooks/useClientLayoutEffect';
import { ErrorHandler } from '../lib/ErrorHandler';
import { widgetTelemetry } from '../lib/widgetTelemetry';
import { createCallbackObservations } from '../lib/callbackObservations';
import { createSwapStatusObserver } from '../lib/swapStatusObserver';
const CallbackContext = createContext(undefined);
function reportCallbackError(caught) {
    const error = caught instanceof Error ? caught : new Error(String(caught));
    ErrorHandler({
        type: 'CallbackError',
        name: error.name,
        message: error.message,
        stack: error.stack,
        cause: caught,
    });
}
export function CallbackProvider({ children, callbacks }) {
    const [observations] = useState(createCallbackObservations);
    const [backendStatuses] = useState(createSwapStatusObserver);
    const callbacksRef = useRef(callbacks);
    useClientLayoutEffect(() => { callbacksRef.current = callbacks; }, [callbacks]);
    // Status/lifecycle effects must not rerun just because a host replaces its callbacks.
    const onSwapStatusChange = useCallback((event) => {
        if (!backendStatuses.observe(event))
            return;
        try {
            callbacksRef.current?.onSwapStatusChange?.(event);
        }
        catch (error) {
            reportCallbackError(error);
        }
    }, [backendStatuses]);
    const onSwapLifecycle = useCallback((event) => {
        event = { ...event, amountMode: widgetTelemetry.amountInputMethod(event) };
        // Creation is an explicit operation result. Wallet prompts/phase observations never
        // establish or reset backend history.
        if (event.step === 'swap_created' && event.swapId)
            backendStatuses.created(event.swapId);
        widgetTelemetry.lifecycle(event);
        if (!observations.lifecycle(event))
            return;
        try {
            callbacksRef.current?.onSwapLifecycle?.(event);
        }
        catch (error) {
            reportCallbackError(error);
        }
    }, [observations, backendStatuses]);
    const telemetryRef = useRef(callbacks?.onTelemetry);
    useClientLayoutEffect(() => { telemetryRef.current = callbacks?.onTelemetry; }, [callbacks?.onTelemetry]);
    const telemetryEnabled = !!callbacks?.onTelemetry;
    useClientLayoutEffect(() => widgetTelemetry.register(telemetryEnabled
        ? event => telemetryRef.current?.(event) : undefined), [telemetryEnabled]);
    const value = useMemo(() => {
        return {
            onTelemetry: event => { try {
                callbacks?.onTelemetry?.(event);
            }
            catch { /* optional telemetry */ } },
            onFormChange: (formData) => { try {
                callbacks?.onFormChange?.(formData);
            }
            catch (error) {
                reportCallbackError(error);
            } },
            onSwapCreate: (swapData) => {
                backendStatuses.created(swapData.swap.id);
                try {
                    callbacks?.onSwapCreate?.(swapData);
                }
                catch (error) {
                    reportCallbackError(error);
                }
            },
            onSwapComplete: (swapData) => { try {
                callbacks?.onSwapComplete?.(swapData);
            }
            catch (error) {
                reportCallbackError(error);
            } },
            onSwapModalStateChange: (open) => {
                if (open)
                    observations.reset();
                try {
                    callbacks?.onSwapModalStateChange?.(open);
                }
                catch (error) {
                    reportCallbackError(error);
                }
            },
            onBackClick: () => {
                observations.reset();
                try {
                    callbacks?.onBackClick?.();
                }
                catch (error) {
                    reportCallbackError(error);
                }
            },
            onSwapStatusChange,
            onSwapLifecycle,
            onMenuNavigationChange: (path) => { try {
                callbacks?.onMenuNavigationChange?.(path);
            }
            catch (error) {
                reportCallbackError(error);
            } },
        };
    }, [callbacks, onSwapStatusChange, onSwapLifecycle, observations, backendStatuses]);
    return (_jsx(CallbackContext.Provider, { value: value, children: children }));
}
/** Presentation previews can render without mounting live callback observers. */
export function useOptionalCallbacks() {
    return useContext(CallbackContext);
}
export function useCallbacks() {
    const context = useOptionalCallbacks();
    if (!context) {
        throw new Error('useCallbacks must be used within a CallbackProvider');
    }
    return context;
}
